import { Suspense, lazy, useEffect, useState, Component, type ReactNode } from "react";
import * as Sentry from "@sentry/react";
import { QueryClient, QueryClientProvider, useQuery } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes, Navigate, useLocation } from "react-router-dom";
import { pageview, identify, getAnalyticsConsent } from "@/lib/analytics";
import { CookieBanner } from "@/components/CookieBanner";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider, useAuth } from "@/lib/auth";
import { fetchFormattingPreferences } from "@/lib/api";
import { saveFormattingSettings } from "@/lib/format";
import { isExpiredSubscriptionStatus } from "@/lib/route-access";
import AppShell from "@/components/AppShell";
import { RouteGate } from "@/components/RouteGate";

const Dashboard = lazy(() => import("./pages/Dashboard"));
const Findings = lazy(() => import("./pages/Findings"));
const SuspiciousTransactions = lazy(() => import("./pages/SuspiciousTransactions"));
const Accounts = lazy(() => import("./pages/Accounts"));
const Payees = lazy(() => import("./pages/Payees"));
const MoneyFlow = lazy(() => import("./pages/MoneyFlow"));
const MonthlyPayments = lazy(() => import("./pages/MonthlyPayments"));
const Categories = lazy(() => import("./pages/Categories"));
const Settings = lazy(() => import("./pages/Settings"));
const Account = lazy(() => import("./pages/Account"));
const Share = lazy(() => import("./pages/Share"));
const Support = lazy(() => import("./pages/Support"));
const Help = lazy(() => import("./pages/Help"));
const InvitePage = lazy(() => import("./pages/InvitePage"));
const InviteAccept = lazy(() => import("./pages/InviteAccept"));
const Login = lazy(() => import("./pages/Login"));
const SignUp = lazy(() => import("./pages/SignUp"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const EmailVerification = lazy(() => import("./pages/EmailVerification"));
const AuthAction = lazy(() => import("./pages/AuthAction"));
const AuthCallback = lazy(() => import("./pages/AuthCallback"));
const AccountRecovery = lazy(() => import("./pages/AccountRecovery"));
const FinancialScore = lazy(() => import("./pages/FinancialScore"));
const AdminLayout = lazy(() => import("./pages/Admin").then((module) => ({ default: module.AdminLayout })));
const AdminOverview = lazy(() => import("./pages/Admin").then((module) => ({ default: module.AdminOverview })));
const AdminUsers = lazy(() => import("./pages/Admin").then((module) => ({ default: module.AdminUsers })));
const AdminFindings = lazy(() => import("./pages/Admin").then((module) => ({ default: module.AdminFindings })));
const AdminDetectors = lazy(() => import("./pages/Admin").then((module) => ({ default: module.AdminDetectors })));
const AdminTags = lazy(() => import("./pages/Admin").then((module) => ({ default: module.AdminTags })));
const AdminGlobalPayees = lazy(() => import("./pages/Admin").then((module) => ({ default: module.AdminGlobalPayees })));
const AdminAIUsage = lazy(() => import("./pages/Admin").then((module) => ({ default: module.AdminAIUsage })));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Transient/cold-start failures are retried with backoff in the transport
      // layer (lib/http.ts retryingFetch), which also covers mutations. Errors
      // that reach react-query are therefore final (4xx app errors or retries
      // already exhausted), so we surface them immediately rather than retry again.
      retry: 0,
      staleTime: 30_000,
      // Always refetch on mount so pages can gate their loader on
      // isFetchedAfterMount and never render a stale/empty cache (e.g. right
      // after login or SPA navigation) as if it were live data.
      refetchOnMount: "always",
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  },
});

function FormattingPreferencesBootstrap() {
  const { user, loading, agencyContext } = useAuth();

  useQuery({
    queryKey: ["formatting-preferences-bootstrap", user?.uid || ""],
    queryFn: fetchFormattingPreferences,
    enabled: !!user && !loading && !!agencyContext && !isExpiredSubscriptionStatus(agencyContext.subscription_status),
    onSuccess: (prefs) => {
      saveFormattingSettings({
        currency: prefs.currency as Parameters<typeof saveFormattingSettings>[0]["currency"],
        currencyPlacement: prefs.currency_placement,
        numberFormat: prefs.number_format,
        dateFormat: prefs.date_format,
        hideAmounts: prefs.hide_amounts,
      });
    },
    staleTime: 5 * 60_000,
  });

  return null;
}

function PageViewTracker() {
  const location = useLocation();
  useEffect(() => {
    pageview(location.pathname + location.search);
  }, [location]);
  return null;
}

function UserIdentifier() {
  const { user, agencyContext } = useAuth();
  useEffect(() => {
    if (!user) return;
    identify(user.uid, {
      email: user.email,
      agency_id: agencyContext?.agency_id,
      agency_name: agencyContext?.agency_name,
      plan: agencyContext?.plan,
    });
  }, [user, agencyContext]);
  return null;
}

function CookieConsentBanner() {
  const [show, setShow] = useState(getAnalyticsConsent() === null);
  if (!show) return null;
  return <CookieBanner onDismiss={() => setShow(false)} />;
}

function RedirectWithParams({ to }: { to: string }) {
  const location = useLocation();
  return <Navigate to={`${to}${location.search}${location.hash}`} replace />;
}

function isChunkLoadError(error: unknown): boolean {
  const msg = (error instanceof Error ? error.message : String(error)).toLowerCase();
  return (
    msg.includes("failed to fetch dynamically imported module") ||
    msg.includes("error loading dynamically imported module") ||
    msg.includes("importing a module script failed") ||
    msg.includes("chunkloaderror")
  );
}

function reloadOnce(): void {
  const key = "ftk_chunk_reload_at";
  const last = Number(sessionStorage.getItem(key) ?? 0);
  if (Date.now() - last < 30_000) return;
  sessionStorage.setItem(key, String(Date.now()));
  window.location.reload();
}

class ChunkErrorBoundary extends Component<
  { children: ReactNode },
  { crashed: boolean; chunk: boolean }
> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { crashed: false, chunk: false };
  }
  static getDerivedStateFromError(error: unknown) {
    return { crashed: true, chunk: isChunkLoadError(error) };
  }
  componentDidCatch(error: Error) {
    if (isChunkLoadError(error)) {
      reloadOnce();
      return;
    }
    // A render error inside a lazy route. This boundary sits BELOW the root
    // Sentry.ErrorBoundary and intercepts the error, so it must report here or
    // the crash stays invisible (and the page just goes blank).
    Sentry.captureException(error);
    // eslint-disable-next-line no-console
    console.error("Route render error", error);
  }
  render() {
    if (this.state.crashed) {
      // Chunk errors trigger a reload; show nothing while it happens.
      if (this.state.chunk) return null;
      // Render errors: show a recoverable message instead of a blank page.
      return (
        <div className="flex min-h-[60vh] flex-col items-center justify-center gap-3 p-6 text-center">
          <p className="text-sm text-muted-foreground">Something went wrong loading this page.</p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="rounded-md border border-border px-3 py-1.5 text-sm font-medium hover:bg-accent"
          >
            Reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

function RouteFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <div role="status" aria-label="Loading…" className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
    </div>
  );
}

function ShellRouteFallback() {
  return (
    <div className="flex min-h-full items-center justify-center p-6">
      <div role="status" aria-label="Loading…" className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
    </div>
  );
}

function RouteLoader({
  children,
  shell = false,
}: {
  children: ReactNode;
  shell?: boolean;
}) {
  return (
    <ChunkErrorBoundary>
      <Suspense fallback={shell ? <ShellRouteFallback /> : <RouteFallback />}>{children}</Suspense>
    </ChunkErrorBoundary>
  );
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <FormattingPreferencesBootstrap />
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter future={{ v7_relativeSplatPath: true, v7_startTransition: true }}>
          <PageViewTracker />
          <UserIdentifier />
          <CookieConsentBanner />
          <Routes>
            <Route path="/invite" element={<RouteLoader><InvitePage /></RouteLoader>} />
            <Route path="/invite/accept" element={<RouteLoader><InviteAccept /></RouteLoader>} />
            <Route path="/login" element={<RouteLoader><Login /></RouteLoader>} />
            <Route path="/signup" element={<RouteLoader><SignUp /></RouteLoader>} />
            <Route path="/forgot-password" element={<RouteLoader><ForgotPassword /></RouteLoader>} />
            <Route path="/reset-password" element={<RouteLoader><ResetPassword /></RouteLoader>} />
            <Route path="/verify-email" element={<RouteLoader><EmailVerification /></RouteLoader>} />
            <Route path="/auth/action" element={<RouteLoader><AuthAction /></RouteLoader>} />
            <Route path="/auth/callback" element={<RouteLoader><AuthCallback /></RouteLoader>} />

            <Route element={<RouteGate requireDeletionPending allowDeletionPending />}>
              <Route path="/account/recover" element={<RouteLoader><AccountRecovery /></RouteLoader>} />
              <Route path="/account-recovery" element={<Navigate to="/account/recover" replace />} />
              <Route path="/recover-account" element={<Navigate to="/account/recover" replace />} />
            </Route>

            <Route element={<RouteGate />}>
              <Route element={<AppShell />}>
                <Route path="/account" element={<RouteLoader shell><Account /></RouteLoader>} />
                <Route path="/billing" element={<Navigate to="/account?tab=billing" replace />} />
                <Route path="/settings" element={<RouteLoader shell><Settings /></RouteLoader>} />
                <Route path="/support" element={<RouteLoader shell><Support /></RouteLoader>} />
                <Route path="/help" element={<RouteLoader shell><Help /></RouteLoader>} />
                <Route path="/accounts" element={<RouteLoader shell><Accounts /></RouteLoader>} />
                <Route path="/connect/plan" element={<Navigate to="/settings?tab=connectors" replace />} />
                <Route path="/tools" element={<Navigate to="/settings?tab=detectors" replace />} />
                <Route path="/family" element={<Navigate to="/settings?tab=family" replace />} />
                <Route path="/profile" element={<Navigate to="/account?tab=profile" replace />} />
                <Route path="/detectors" element={<Navigate to="/settings?tab=detectors" replace />} />
                <Route path="/connectors" element={<Navigate to="/settings?tab=connectors" replace />} />

                <Route element={<RouteGate requireActiveSubscription />}>
                  <Route path="/" element={<Navigate to="/dashboard" replace />} />
                  <Route path="/dashboard" element={<RouteLoader shell><Dashboard /></RouteLoader>} />
                  <Route path="/findings" element={<RouteLoader shell><Findings /></RouteLoader>} />
                  <Route path="/suspicious" element={<RouteLoader shell><SuspiciousTransactions /></RouteLoader>} />
                  <Route path="/payees" element={<RouteLoader shell><Payees /></RouteLoader>} />
                  <Route path="/money-flow" element={<RouteLoader shell><MoneyFlow /></RouteLoader>} />
                  <Route path="/monthly-payments" element={<RouteLoader shell><MonthlyPayments /></RouteLoader>} />
                  <Route path="/monthly-spend" element={<RedirectWithParams to="/money-flow" />} />
                  <Route path="/one-time" element={<RedirectWithParams to="/monthly-payments" />} />
                  <Route path="/categories" element={<RouteLoader shell><Categories /></RouteLoader>} />
                  <Route path="/financial-score" element={<RouteLoader shell><FinancialScore /></RouteLoader>} />
                  <Route path="/share" element={<RouteLoader shell><Share /></RouteLoader>} />
                  <Route path="/share-and-earn" element={<Navigate to="/share" replace />} />

                  <Route path="/admin" element={<RouteLoader shell><AdminLayout /></RouteLoader>}>
                    <Route index element={<RouteLoader shell><AdminOverview /></RouteLoader>} />
                    <Route path="users" element={<RouteLoader shell><AdminUsers /></RouteLoader>} />
                    <Route path="findings" element={<RouteLoader shell><AdminFindings /></RouteLoader>} />
                    <Route path="detectors" element={<RouteLoader shell><AdminDetectors /></RouteLoader>} />
                    <Route path="tags" element={<RouteLoader shell><AdminTags /></RouteLoader>} />
                    <Route path="global-payees" element={<RouteLoader shell><AdminGlobalPayees /></RouteLoader>} />
                    <Route path="ai-usage" element={<RouteLoader shell><AdminAIUsage /></RouteLoader>} />
                  </Route>
                </Route>
              </Route>
            </Route>

            <Route path="*" element={<RouteLoader><NotFound /></RouteLoader>} />
          </Routes>
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
